import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Info, Check, AlertCircle, HelpCircle } from 'lucide-react';
import { QRTypeFields } from '../config/qrTypes';
import stringSimilarity from 'string-similarity';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  title: {
    color: '#111827',
    fontSize: '1.5rem',
    fontWeight: '600',
    margin: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  thead: {
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    color: '#374151',
    fontSize: '0.875rem',
    fontWeight: '600',
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    '&:not(:last-child)': {
      borderBottom: '1px solid #e5e7eb',
    },
    '&:hover': {
      backgroundColor: '#f9fafb',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '0.875rem',
    color: '#111827',
    verticalAlign: 'top',
  },
  fieldCell: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    minWidth: '200px',
  },
  fieldInfo: {
    flex: 1,
  },
  fieldName: {
    color: '#111827',
    fontSize: '0.875rem',
    fontWeight: '600',
    marginBottom: '4px',
  },
  fieldDescription: {
    color: '#6b7280',
    fontSize: '0.75rem',
    lineHeight: '1.4',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    fontSize: '0.875rem',
    color: '#111827',
    backgroundColor: '#fff',
    transition: 'all 150ms ease',
    cursor: 'pointer',
    outline: 'none',
    '&:focus': {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 2px rgba(37,99,235,0.2)',
    },
  },
  multiSelect: {
    height: '120px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    fontSize: '0.875rem',
    color: '#111827',
    transition: 'all 150ms ease',
    outline: 'none',
    '&:focus': {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 2px rgba(37,99,235,0.2)',
    },
  },
  validationMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.75rem',
    marginTop: '4px',
  },
  validSuccess: {
    color: '#059669',
  },
  validError: {
    color: '#dc2626',
  },
  previewContainer: {
    marginTop: '4px',
    padding: '8px',
    backgroundColor: '#f9fafb',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: '#6b7280',
  },
  previewLabel: {
    color: '#111827',
    fontWeight: '500',
    marginBottom: '2px',
  },
  tooltip: {
    position: 'relative',
    display: 'inline-flex',
    cursor: 'help',
    '&:hover::after': {
      content: 'attr(data-tip)',
      position: 'absolute',
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginLeft: '8px',
      padding: '6px 12px',
      backgroundColor: '#1f2937',
      color: '#fff',
      fontSize: '0.75rem',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      zIndex: 10,
      maxWidth: '300px',
    },
  },
  note: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    padding: '12px 16px',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    color: '#4b5563',
    fontSize: '0.875rem',
    lineHeight: '1.4',
  },
  helpIcon: {
    color: '#6b7280',
    cursor: 'help',
  },
};

// Field descriptions and validation rules for each QR code type
const FieldConfig = {
  plain_text: {
    columns: {
      description: "Select one or more columns to combine in the QR code",
      required: true,
      validate: (value) => Array.isArray(value) && value.length > 0,
      tooltip: "The selected columns will be combined using the specified separator",
    },
    separator: {
      description: "Character(s) to separate the selected columns",
      required: false,
      validate: () => true,
      tooltip: "Common separators: comma (,) space ( ) pipe (|)",
      default: ",",
    },
  },
  email: {
    email: {
      description: "Email address of the recipient",
      required: true,
      validate: (value, data) => {
        const firstRow = data[0] && data[0][value];
        return firstRow && firstRow.includes('@');
      },
      tooltip: "Must be a valid email format (e.g., user@example.com)",
    },
    subject: {
      description: "Subject line of the email (optional)",
      required: false,
      validate: () => true,
      tooltip: "Will be URL encoded in the QR code",
    },
    body: {
      description: "Content of the email (optional)",
      required: false,
      validate: () => true,
      tooltip: "Will be URL encoded in the QR code",
    },
  },
  contact: {
    name: {
      description: "Full name of the contact",
      required: true,
      validate: (value) => !!value,
      tooltip: "Will be used as the display name in the contact card",
    },
    phone: {
      description: "Phone number with country code",
      required: false,
      validate: (value, data) => {
        if (!value) return true;
        const firstRow = data[0] && data[0][value];
        return !firstRow || /^[+\d\s-()]+$/.test(firstRow);
      },
      tooltip: "Include country code for international format (e.g., +1 234 567 8900)",
    },
    email: {
      description: "Email address of the contact",
      required: false,
      validate: (value, data) => {
        if (!value) return true;
        const firstRow = data[0] && data[0][value];
        return !firstRow || firstRow.includes('@');
      },
      tooltip: "Must be a valid email format if provided",
    },
  },
  calendar: {
    title: {
      description: "Title or summary of the event",
      required: true,
      validate: (value) => !!value,
      tooltip: "Will be displayed as the event title in calendar apps",
    },
    start_date: {
      description: "Start date and time of the event",
      required: true,
      validate: (value, data) => {
        const firstRow = data[0] && data[0][value];
        return firstRow && !isNaN(Date.parse(firstRow));
      },
      tooltip: "Must be in a valid date format (e.g., YYYY-MM-DD HH:mm)",
    },
    end_date: {
      description: "End date and time of the event",
      required: true,
      validate: (value, data) => {
        const firstRow = data[0] && data[0][value];
        return firstRow && !isNaN(Date.parse(firstRow));
      },
      tooltip: "Must be in a valid date format (e.g., YYYY-MM-DD HH:mm)",
    },
  },
  product: {
    product_name: {
      description: "Name or title of the product",
      required: true,
      validate: (value) => !!value,
      tooltip: "The main display name of the product",
    },
    product_id: {
      description: "Product ID or SKU (Stock Keeping Unit)",
      required: true,
      validate: (value) => !!value,
      tooltip: "A unique identifier for the product",
    },
    description: {
      description: "Brief description of the product",
      required: false,
      validate: () => true,
      tooltip: "Additional details about the product",
    },
    category: {
      description: "Category or type of product",
      required: false,
      validate: () => true,
      tooltip: "Product classification (e.g., Electronics, Clothing, Food)",
    },
    brand: {
      description: "Brand or manufacturer name",
      required: false,
      validate: () => true,
      tooltip: "Company or brand that makes/sells the product",
    },
  },
  pricing_inventory: {
    price: {
      description: "Retail or sale price of the product",
      required: true,
      validate: (value, data) => {
        if (!value) return false;
        const firstRow = data[0] && data[0][value];
        return firstRow && !isNaN(parseFloat(firstRow));
      },
      tooltip: "The price of the product (numeric value)",
    },
    currency: {
      description: "Currency used for pricing",
      required: true,
      validate: (value) => !!value,
      tooltip: "Currency code (e.g., USD, EUR)",
    },
    quantity: {
      description: "Available stock or quantity",
      required: true,
      validate: (value, data) => {
        if (!value) return false;
        const firstRow = data[0] && data[0][value];
        return firstRow && !isNaN(parseInt(firstRow));
      },
      tooltip: "Number of items in stock",
    },
    batch_number: {
      description: "Batch or lot number for tracking",
      required: false,
      validate: () => true,
      tooltip: "Production batch identifier (common in food, pharmaceuticals)",
    },
  },
  tracking_logistics: {
    serial_number: {
      description: "Unique identifier for individual items",
      required: true,
      validate: (value) => !!value,
      tooltip: "Serial number for tracking individual items (e.g., electronics, high-value products)",
    },
    barcode: {
      description: "Standardized barcode number",
      required: true,
      validate: (value) => !!value,
      tooltip: "UPC, EAN, or other barcode numbers for scanning and inventory tracking",
    },
    expiration_date: {
      description: "Expiration date for perishable goods",
      required: false,
      validate: () => true,
      tooltip: "Date when the product expires (for food, medicine, etc.)",
    },
    manufacturing_date: {
      description: "Date of manufacture",
      required: false,
      validate: () => true,
      tooltip: "Date when the product was manufactured",
    },
    shipping_info: {
      description: "Shipping and tracking details",
      required: false,
      validate: () => true,
      tooltip: "Tracking numbers, shipping dates, or destination details",
    },
  },
};

const MapFields = ({ qrType, csvHeaders, csvData, mappedFields, setMappedFields }) => {
  const [validationState, setValidationState] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Field patterns for matching
  const fieldPatterns = useMemo(() => ({
    serial_number: [
      "serial_number", "serial", "serial_no", "serial_num", "item_serial", "product_serial",
      "unique_id", "unique_identifier", "item_id", "individual_id"
    ],
    barcode: [
      "barcode", "upc", "ean", "gtin", "barcode_number", "upc_code", "ean_code",
      "scan_code", "product_code", "item_code", "universal_product_code"
    ],
    expiration_date: [
      "expiration_date", "expiry_date", "expiry", "exp_date", "expires_on",
      "best_before", "use_by", "expiration", "exp"
    ],
    manufacturing_date: [
      "manufacturing_date", "mfg_date", "manufacture_date", "production_date",
      "made_on", "mfg", "date_of_manufacture", "manufactured_on"
    ],
    shipping_info: [
      "shipping_info", "tracking_number", "tracking_info", "shipment_details",
      "shipping_details", "tracking_id", "shipment_id", "shipping_tracking",
      "destination", "shipping_destination", "delivery_info"
    ]
  }), []);

  // Find best matching header for a field
  const findBestMatch = useCallback((field, headers) => {
    const patterns = fieldPatterns[field] || [field];
    const lowerHeaders = headers.map(h => h.toLowerCase());
    
    // Try exact matches first
    for (const pattern of patterns) {
      const exactIndex = lowerHeaders.findIndex(h => h === pattern);
      if (exactIndex !== -1) {
        return {
          bestMatch: {
            target: headers[exactIndex],
            rating: 1
          }
        };
      }
    }

    // Try partial matches using string similarity
    let bestRating = 0;
    let bestHeader = null;

    headers.forEach(header => {
      patterns.forEach(pattern => {
        const rating = stringSimilarity.compareTwoStrings(
          header.toLowerCase(),
          pattern.toLowerCase()
        );
        if (rating > bestRating) {
          bestRating = rating;
          bestHeader = header;
        }
      });
    });

    return {
      bestMatch: {
        target: bestHeader || "",
        rating: bestRating
      }
    };
  }, [fieldPatterns]);

  // Auto-map fields when QR type or CSV headers change
  useEffect(() => {
    if (csvHeaders && csvHeaders.length > 0) {
      const newMappings = {};
      const requiredFields = QRTypeFields[qrType] || [];

      requiredFields.forEach(field => {
        const bestMatch = findBestMatch(field, csvHeaders);
        if (bestMatch.bestMatch.rating > 0.4) {
          newMappings[field] = bestMatch.bestMatch.target;
        }
      });

      setMappedFields(prev => ({
        ...prev,
        ...newMappings
      }));
    }
  }, [qrType, csvHeaders, setMappedFields, findBestMatch]);

  // Validate fields when mappings change
  useEffect(() => {
    if (csvData && csvData.length > 0) {
      const validateFields = () => {
        const config = FieldConfig[qrType] || {};
        const newValidationState = {};

        Object.entries(config).forEach(([field, fieldConfig]) => {
          const value = mappedFields[field];
          const isValid = fieldConfig.validate(value, csvData);
          newValidationState[field] = {
            isValid,
            message: isValid 
              ? (value ? "Field mapped successfully" : "Optional field") 
              : `Invalid ${field.replace('_', ' ')}`,
          };
        });

        setValidationState(newValidationState);
      };

      validateFields();
    }
  }, [mappedFields, csvData, qrType]);

  const handleMappingChange = (field, e) => {
    if (qrType === 'plain_text' && field === 'columns') {
      const options = e.target.options;
      const selectedValues = [];
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
          selectedValues.push(options[i].value);
        }
      }
      setSelectedColumns(selectedValues);
      setMappedFields(prev => ({
        ...prev,
        columns: selectedValues,
      }));
    } else {
      setMappedFields(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    }
  };

  // Get preview of mapped data
  const getPreview = (field) => {
    if (!csvData || csvData.length === 0) return null;
    
    const value = mappedFields[field];
    if (!value) return null;

    if (field === 'columns' && Array.isArray(value)) {
      const separator = mappedFields.separator || ',';
      return value.map(col => csvData[0][col]).join(separator);
    }
    
    return csvData[0][value];
  };

  const config = FieldConfig[qrType] || {};
  const fields = Object.keys(config);

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>
        Map Fields for {qrType.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')}
      </h2>

      <table style={styles.table}>
        <thead style={styles.thead}>
          <tr>
            <th style={styles.th}>QR Code Field</th>
            <th style={styles.th}>CSV Column</th>
            <th style={styles.th}>Preview</th>
            <th style={styles.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const fieldConfig = config[field];
            const validation = validationState[field] || {};
            const preview = getPreview(field);

            return (
              <tr key={field} style={styles.tr}>
                <td style={styles.td}>
                  <div style={styles.fieldCell}>
                    <div style={styles.fieldInfo}>
                      <div style={styles.fieldName}>
                        {field.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                        {fieldConfig.required && ' *'}
                      </div>
                      <div style={styles.fieldDescription}>
                        {fieldConfig.description}
                      </div>
                    </div>
                    <div 
                      style={styles.tooltip} 
                      data-tip={fieldConfig.tooltip}
                    >
                      <HelpCircle size={16} style={styles.helpIcon} />
                    </div>
                  </div>
                </td>
                <td style={styles.td}>
                  {field === 'columns' ? (
                    <select
                      multiple
                      value={selectedColumns}
                      onChange={(e) => handleMappingChange(field, e)}
                      style={{ ...styles.select, ...styles.multiSelect }}
                    >
                      {csvHeaders.map((header) => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                  ) : field === 'separator' ? (
                    <input
                      type="text"
                      value={mappedFields.separator || ','}
                      onChange={(e) => handleMappingChange(field, { 
                        target: { value: e.target.value } 
                      })}
                      placeholder="Enter separator"
                      style={styles.input}
                    />
                  ) : (
                    <select
                      value={mappedFields[field] || ""}
                      onChange={(e) => handleMappingChange(field, e)}
                      style={styles.select}
                    >
                      <option value="">-- Select CSV Column --</option>
                      {csvHeaders.map((header) => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                  )}
                </td>
                <td style={styles.td}>
                  {preview && (
                    <div style={styles.previewContainer}>
                      {preview}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  {validation.isValid !== undefined && (
                    <div 
                      style={{
                        ...styles.validationMessage,
                        ...(validation.isValid ? styles.validSuccess : styles.validError)
                      }}
                    >
                      {validation.isValid ? (
                        <Check size={14} />
                      ) : (
                        <AlertCircle size={14} />
                      )}
                      {validation.message}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={styles.note}>
        <Info size={16} />
        <div>
          {qrType === 'plain_text' 
            ? 'Select one or more columns and specify a separator to combine them. The preview shows how your data will be combined.'
            : `All required fields (*) must be mapped and valid. The preview shows the first row of your data.`}
        </div>
      </div>
    </div>
  );
};

export default MapFields;
